import React from "react";
import { api } from "@/lib/config";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Overview from "./admin/Overview";
import Submissions from "./admin/Submissions";
import GalleryAdmin from "./admin/GalleryAdmin";
import Estimates from "./admin/Estimates";
import CardSettings from "./admin/CardSettings";
import { LogOut } from "lucide-react";

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = React.useState(location.state?.user || null);
  const [loading, setLoading] = React.useState(!location.state?.user);

  React.useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch {
        navigate("/admin/login", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [user, navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[color:var(--brand-woodDark)]">Loading admin...</div>;

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-[color:var(--brand-wood)]">Admin</div>
          <h1 className="mt-2 font-display text-3xl">Welcome, {user?.name?.split(" ")[0] || "there"}.</h1>
        </div>
        <div className="flex items-center gap-3">
          {user?.picture && <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover"/>}
          <button onClick={logout} data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm hover:bg-black/5 transition-colors">
            <LogOut className="h-4 w-4"/> Sign out
          </button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-transparent border border-black/10 rounded-full p-1 h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">Overview</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="admin-tab-submissions" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">Submissions</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="admin-tab-gallery" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">Gallery</TabsTrigger>
          <TabsTrigger value="estimates" data-testid="admin-tab-estimates" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">Estimates</TabsTrigger>
          <TabsTrigger value="card" data-testid="admin-tab-card" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">Card</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-8"><Overview/></TabsContent>
        <TabsContent value="submissions" className="mt-8"><Submissions/></TabsContent>
        <TabsContent value="gallery" className="mt-8"><GalleryAdmin/></TabsContent>
        <TabsContent value="estimates" className="mt-8"><Estimates/></TabsContent>
        <TabsContent value="card" className="mt-8"><CardSettings/></TabsContent>
      </Tabs>
    </div>
  );
}
